import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setAuthToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    // R31 Google Auth: se la URL contiene un session_id Emergent (hash fragment),
    // siamo nel mezzo dell'OAuth callback. La pagina /auth/google/callback
    // gestirà lo scambio session_id→JWT cookie e poi chiamerà refresh().
    // Saltare il /auth/me qui evita un 401 race-condition (cookie non ancora set).
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    // Helper: tenta il POST. Se fallisce con errore di rete / 5xx / timeout
    // (NON con 401/403/422 che sono errori applicativi deterministici), riprova
    // una volta dopo 500ms. Risolve il problema "primo click errore, secondo
    // click OK" causato dal cold-start del backend dietro CDN/proxy.
    const attempt = async () => {
      return await api.post("/auth/login", { email, password });
    };
    let resp;
    try {
      resp = await attempt();
    } catch (e) {
      const status = e.response?.status;
      const isAppError = status === 401 || status === 403 || status === 422 || status === 429;
      if (!isAppError) {
        // Network error / 5xx / timeout → silent retry una volta
        await new Promise((r) => setTimeout(r, 500));
        try {
          resp = await attempt();
        } catch (e2) {
          return { ok: false, error: formatApiError(e2.response?.data?.detail) || e2.message || "Errore di rete. Verifica la connessione e riprova." };
        }
      } else {
        return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
      }
    }
    try {
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith("oggi_toast_"))
        .forEach((k) => sessionStorage.removeItem(k));
    } catch { /* ignore */ }
    // iOS fallback: salva il bearer token in localStorage. Su browser dove i
    // cookie httpOnly funzionano è ridondante (axios li manda via withCredentials),
    // ma su iOS Safari ITP / in-app browser FB/IG i cookie possono essere bloccati
    // e in quel caso il Bearer è l'unico modo di autenticarsi.
    if (resp.data?.access_token) {
      setAuthToken(resp.data.access_token);
    }
    setUser(resp.data);
    return { ok: true };
  };

  // Pulisce SOLO i cookie httpOnly applicativi via endpoint backend +
  // localStorage/sessionStorage frontend. NON tocca il service worker né le
  // cache HTTP del browser (non installati su questo dominio + causavano
  // comportamenti imprevedibili su iOS PWA).
  const clearSession = async () => {
    try { await api.post("/auth/clear-session"); } catch { /* ignore */ }
    try { localStorage.clear(); } catch { /* ignore */ }
    try { sessionStorage.clear(); } catch { /* ignore */ }
    setUser(false);
    // Hard reload con flag visivo per mostrare toast di conferma
    window.location.replace("/login?cleared=1");
  };

  const register = async (email, password, name, role = "operator") => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name, role });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    try {
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith("oggi_toast_"))
        .forEach((k) => sessionStorage.removeItem(k));
    } catch { /* ignore */ }
    setAuthToken(null); // pulisce il bearer iOS
    setUser(false);
  };

  const stopImpersonation = async () => {
    try {
      await api.post("/orgs/impersonate/stop");
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    } finally {
      // Sempre allineare la UI allo stato reale del server (anche su errore transitorio)
      await refresh();
    }
  };

  // Esposto per update ottimistici (es. SA scope switch): la UI si aggiorna
  // immediatamente senza attendere il roundtrip /auth/me.
  const patchUser = useCallback((patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  return (
    <AuthContext.Provider value={{ user, checking, login, register, logout, refresh, patchUser, stopImpersonation, clearSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
